import { FeedbackMode } from '@panaya/ui/p-like-dislike';
import { ActionSuggestion } from './actionSuggestion';
import { Attachment } from './attachment.model';

export interface UserPromptData {
    message: string;
    context: string;
    file?: File;
    /** Per-prompt request-context fields merged by the api on this prompt only (e.g. `attachments`). */
    contextExtras?: Record<string, unknown>;
    /** The chips to show on the user's message in the log. */
    attachments?: Attachment[];
}

export interface InternalPromptData {
    message: string;
    hidden: boolean;
    showLoading?: boolean;
    /** Per-prompt request-context fields merged by the api on this prompt only (e.g. `canvas.action`). */
    contextExtras?: Record<string, unknown>;
}

export interface UserPrompt extends UserPromptData {
    source: 'user';
}

export interface InternalPrompt extends InternalPromptData {
    source: 'internal';
}

export type Prompt = UserPrompt | InternalPrompt;

// #region Chat Log Types

export type MessageSource = 'assistant' | 'user';

/**
 * Factory functions to avoid typing source manually
 */
export const createUserPrompt = (data: UserPromptData): UserPrompt => ({
    ...data,
    source: 'user'
});

export const createInternalPrompt = (data: InternalPromptData): InternalPrompt => ({
    ...data,
    source: 'internal'
});

interface ChatMessageOf<Source extends MessageSource = any, Extras extends Record<string, any> = any> {
    source: Source;
    markdownContent: string;
    extras?: Extras;
}

/**
 * Represents a message (prompt) from the user, in a unified format allowing it to be part of a chat log.
 */
export type UserMessage = ChatMessageOf<'user', { context?: string; file?: File; attachments?: Attachment[] }>;
/**
 * Represents a message from the assistant (prompt response), in a unified format allowing it to be part of a chat log.
 */
export type AssistantMessage = ChatMessageOf<
    'assistant',
    { links?: string[]; actionsSuggestions?: ActionSuggestion[]; messageId: string; feedbackMode?: FeedbackMode }
>;

/**
 * Represents a unified format for chat log messages.
 *
 * Thanks to the `source` property, this is a discriminated union.
 * This allows automatic inferrence of the type of `extras` in a message, once the `source` property
 * has been validated.
 */
export type ChatMessage = UserMessage | AssistantMessage;
// #endregion
